"use client"

import { useState, useEffect, useRef } from "react"
import "../css/RecentEvents.css"

// SVG icons as components
const CalendarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect width="18" height="18" x="3" y="4" rx="2" ry="2"></rect>
    <line x1="16" x2="16" y1="2" y2="6"></line>
    <line x1="8" x2="8" y1="2" y2="6"></line>
    <line x1="3" x2="21" y1="10" y2="10"></line>
  </svg>
)

const ExternalLinkIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
    <polyline points="15 3 21 3 21 9"></polyline>
    <line x1="10" x2="21" y1="14" y2="3"></line>
  </svg>
)

const AwardIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="8" r="7"></circle>
    <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
  </svg>
)

const CertificateIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
    <path d="m9 12 2 2 4-4"></path>
  </svg>
)

const ClockIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
)

// Throttle function to limit how often a function can be called
const throttle = (func, limit) => {
  let inThrottle
  return function () {
    const args = arguments

    if (!inThrottle) {
      func.apply(this, args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

const RecentEvents = () => {
  const [expandedIndex, setExpandedIndex] = useState(null)
  const [activeDotIndex, setActiveDotIndex] = useState(null)
  const [scrollProgress, setScrollProgress] = useState(0)
  const timelineEventsRef = useRef([])
  const timelineRef = useRef(null)
  const timelineProgressRef = useRef(null)
  const requestRef = useRef(null)

  // Event data
  const events = [
    { year: "2006", description: " EMS Expo - Top Product Award", link: "#" },
    { year: "2007", description: " EMS Expo - Top Product Award", link: "#" },
    { year: "2018", description: " Flotec has earned MDSAP certification", link: "#" },
    { year: "2018", description: " Flotec has earned ISO 13845:2016 certification", link: "#" },
    { year: "2019", description: " Andrew College's new Respiratory Therapy Program", link: "#" },
    { year: "2019", description: " Unique Device Identifier, Label Identification Code B741 assigned", link: "#" },
    { year: "2019", description: " Flotec manufactures its 1 millionth product on August 21, 2019", link: "#" },
    { year: "2019", description: " Flotec earned CE1434 mark on August 29, 2019", link: "#" },
    { year: "2019", description: " Largest Indianapolis Area Medical Device Manufacturer List", link: "#" },
    { year: "2020", description: " Hose Assembly - Canadian Standard (License # 105590)", link: "#" },
  ]

  const getEventIcon = (event) => {
    if (event.description.includes("Award")) return <AwardIcon />
    if (event.description.includes("Certificate") || event.description.includes("certification"))
      return <CertificateIcon />
    if (event.description.includes("Expired")) return <ClockIcon />
    return <CalendarIcon />
  }

  const handleScroll = () => {
    if (!timelineRef.current || !timelineProgressRef.current) return

    const timelineRect = timelineRef.current.getBoundingClientRect()
    const scrollPosition = window.scrollY
    const windowHeight = window.innerHeight

    // Calculate the timeline's position relative to the viewport
    const timelineTop = timelineRect.top + scrollPosition
    const timelineBottom = timelineRect.bottom + scrollPosition
    const timelineHeight = timelineRect.height

    // Calculate the viewport's middle point
    const viewportMiddle = scrollPosition + (windowHeight / 2)

    // Calculate progress based on how far the viewport middle has progressed through the timeline
    const progressPercentage = Math.max(0, Math.min(100,
      ((viewportMiddle - timelineTop) / timelineHeight) * 100
    ))

    // Set the progress as a percentage (0-100)
    setScrollProgress(progressPercentage)

    // Find the closest event to the middle of the screen
    let closestIndex = null
    let closestDistance = Number.POSITIVE_INFINITY

    timelineEventsRef.current.forEach((element, index) => {
      if (element) {
        const rect = element.getBoundingClientRect()
        const eventMiddle = rect.top + scrollPosition + rect.height / 2
        const distance = Math.abs(eventMiddle - viewportMiddle)

        if (distance < closestDistance) {
          closestDistance = distance
          closestIndex = index
        }
      }
    })

    // Update active dot
    if (closestIndex !== null && closestIndex !== activeDotIndex) {
      setActiveDotIndex(closestIndex)
    }

    // Update which dots are considered "past" based on the middle of the viewport
    const isPastUpdater = index => {
      if (timelineEventsRef.current[index]) {
        const rect = timelineEventsRef.current[index].getBoundingClientRect()
        const eventPosition = rect.top + scrollPosition + rect.height / 2
        return eventPosition < viewportMiddle
      }
      return false
    }
  }

  // Create throttled scroll handler
  const throttledScrollHandler = useRef(throttle(handleScroll, 16)) // ~60fps

  // Add scroll event listener when component mounts
  useEffect(() => {
    window.addEventListener("scroll", throttledScrollHandler.current)

    // Initial check
    handleScroll()

    return () => {
      window.removeEventListener("scroll", throttledScrollHandler.current)
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current)
      }
    }
  }, [])

  // Update refs when events change
  useEffect(() => {
    timelineEventsRef.current = timelineEventsRef.current.slice(0, events.length)
  }, [events])

  return (
    <div className="timeline-container">
      <h2 className="timeline-title">Company Timeline</h2>

      <div className="timeline" ref={timelineRef}>
        {/* Static timeline background line */}
        <div className="timeline-line"></div>

        {/* Dynamic progress line that fills based on scroll */}
        <div
          ref={timelineProgressRef}
          className="timeline-progress-line"
          style={{ height: `${scrollProgress}%` }}
        ></div>

        <div className="timeline-events">
          {events.map((event, index) => {
            const isExpanded = expandedIndex === index
            const isActive = activeDotIndex === index
            const isPast = scrollProgress / 100 > (index + 0.5) / events.length // Dot is considered past when timeline progress passes it

            return (
              <div
                key={index}
                ref={(el) => (timelineEventsRef.current[index] = el)}
                className={`timeline-event ${index % 2 === 0 ? "timeline-event-right" : ""}`}
                onClick={() => setExpandedIndex(isExpanded ? null : index)}
              >
                {/* Timeline dot */}
                <div
                  className={`timeline-dot ${isActive ? "active" : isPast ? "past" : ""}`}
                ></div>

                {/* Content */}
                <div className="timeline-content">
                  <div className={`timeline-card ${isExpanded ? "timeline-card-expanded" : ""}`}>
                    <div className="timeline-card-content">
                      <div className="timeline-icon">{getEventIcon(event)}</div>
                      <div className="timeline-details">
                        <div className="timeline-header">
                          <h3 className="timeline-year">
                              {event.year}
                          </h3>

                          {event.year.includes("2019") && <span className="timeline-badge">Key Year</span>}
                        </div>

                        <p className="timeline-description">{event.description.trim()}</p>

                        {isExpanded && event.description.includes("certification") && (
                          <div className="timeline-expanded-info">
                            <p>Certification demonstrates our commitment to quality and regulatory compliance.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default RecentEvents